import { useState } from 'react';

type FormState = {
  name: string;
  email: string;
  phone: string;
  lessonType: string;
  message: string;
};

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

const initialFormState: FormState = {
  name: '',
  email: '',
  phone: '',
  lessonType: '',
  message: ''
};

function Modal({
  isOpen,
  children
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-6 py-8">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        {children}
      </div>
    </div>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setPrivacyAgreed(false);
  };

  const handleOpenConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.lessonType || !formData.message) {
      setErrorMessage('必須項目を入力してください。');
      setSubmitStatus('error');
      return;
    }

    if (!privacyAgreed) {
      setErrorMessage('プライバシーポリシーに同意してください。');
      setSubmitStatus('error');
      return;
    }

    setErrorMessage('');
    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('lessonType', formData.lessonType);
      formBody.append('message', formData.message);
      formBody.append('privacy', privacyAgreed ? '同意する' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody.toString()
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      resetForm();
      setSubmitStatus('success');
    } catch (error) {
      setErrorMessage('送信に失敗しました。しばらくしてからもう一度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-orange-50/30 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f97316' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full shadow-lg mb-6 border-2 border-orange-300" style={{ borderStyle: 'dashed' }}>
            <i className="ri-mail-send-line text-orange-500"></i>
            <span className="text-orange-600 font-medium" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
              Contact Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Klee One", cursive' }}>
            お問い合わせ
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
            レッスンについてのご質問や体験レッスンのお申し込みは、こちらからどうぞ！
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-3xl shadow-lg border-3 border-orange-200" style={{ borderStyle: 'dashed', borderWidth: '3px' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Klee One", cursive' }}>
                所在地
              </h3>
              <p className="text-gray-600 leading-relaxed" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                〒410-0822<br />
                静岡県沼津市下香貫楊原670番地<br />
                ドルチェヴィータ北館305
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border-3 border-red-200" style={{ borderStyle: 'dashed', borderWidth: '3px' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-pink-400 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Klee One", cursive' }}>
                営業時間
              </h3>
              <p className="text-gray-600 leading-relaxed" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                平日: 10:00 - 20:00<br />
                土日祝: 9:00 - 18:00<br />
                定休日: 不定休
              </p>
            </div>
          </div>

          <form onSubmit={handleOpenConfirm} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border-4 border-orange-300" style={{ borderStyle: 'dashed' }}>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors text-sm"
                  style={{ fontFamily: '"Zen Maru Gothic", sans-serif', borderStyle: 'dashed' }}
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors text-sm"
                  style={{ fontFamily: '"Zen Maru Gothic", sans-serif', borderStyle: 'dashed' }}
                  placeholder="example@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors text-sm"
                  style={{ fontFamily: '"Zen Maru Gothic", sans-serif', borderStyle: 'dashed' }}
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                  ご希望のレッスン <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="lessonType"
                    required
                    value={formData.lessonType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-8 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors appearance-none cursor-pointer text-sm"
                    style={{ fontFamily: '"Zen Maru Gothic", sans-serif', borderStyle: 'dashed' }}
                  >
                    <option value="">選択してください</option>
                    <option value="教室レッスン">教室レッスン</option>
                    <option value="訪問型レッスン">訪問型レッスン</option>
                    <option value="食育プログラム">食育プログラム</option>
                    <option value="体験レッスン">体験レッスン</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-400 pointer-events-none"></i>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                メッセージ <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors resize-none text-sm"
                style={{ fontFamily: '"Zen Maru Gothic", sans-serif', borderStyle: 'dashed' }}
                placeholder="ご質問やご要望をお聞かせください（500文字以内）"
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                {formData.message.length}/500
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyAgreed}
                  onChange={(e) => setPrivacyAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 text-orange-500 border-2 border-orange-300 rounded focus:ring-orange-400 cursor-pointer"
                  style={{ borderStyle: 'dashed' }}
                />
                <span className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
                  <a href="/privacy" className="text-orange-500 hover:text-orange-600 underline font-medium">プライバシーポリシー</a>に同意します <span className="text-red-500">*</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-400 to-red-400 text-white px-8 py-4 rounded-full hover:shadow-xl transition-all whitespace-nowrap cursor-pointer font-bold text-lg shadow-lg"
              style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              入力内容を確認する
            </button>
          </form>
        </div>
      </div>

      <Modal isOpen={submitStatus === 'confirm'}>
        <div className="p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Klee One", cursive' }}>
            入力内容の確認
          </h3>
          <div className="space-y-4 text-sm text-gray-700" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
            <div>
              <p className="font-bold text-orange-600">お名前</p>
              <p>{formData.name}</p>
            </div>
            <div>
              <p className="font-bold text-orange-600">メールアドレス</p>
              <p>{formData.email}</p>
            </div>
            <div>
              <p className="font-bold text-orange-600">電話番号</p>
              <p>{formData.phone || '未入力'}</p>
            </div>
            <div>
              <p className="font-bold text-orange-600">ご希望のレッスン</p>
              <p>{formData.lessonType}</p>
            </div>
            <div>
              <p className="font-bold text-orange-600">メッセージ</p>
              <p className="whitespace-pre-wrap">{formData.message}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 border-2 border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors"
            >
              修正する
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="px-6 py-3 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-full hover:shadow-lg transition-all"
            >
              この内容で送信する
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full border-4 border-orange-100 border-t-orange-500 animate-spin" />
          <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Klee One", cursive' }}>
            送信中です
          </h3>
          <p className="text-gray-600" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
            内容を送信しています。しばらくお待ちください。
          </p>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'success'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Klee One", cursive' }}>
            送信が完了しました
          </h3>
          <p className="text-gray-600" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
            お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
          </p>
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="mt-8 px-6 py-3 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-full hover:shadow-lg transition-all"
          >
            閉じる
          </button>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'error'}>
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-close-line text-3xl text-red-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: '"Klee One", cursive' }}>
            送信できませんでした
          </h3>
          <p className="text-gray-600" style={{ fontFamily: '"Zen Maru Gothic", sans-serif' }}>
            {errorMessage}
          </p>
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="mt-8 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
          >
            戻る
          </button>
        </div>
      </Modal>
    </section>
  );
}
